import omit from "lodash/omit";
import delay from "lodash/delay";
import isNumber from "lodash/isNumber";
import isFunction from "lodash/isFunction";
import keycode from "keycode";
import FocalPoint from "components/FocalPoint";
import onUserInteractionInitiatedPlaybackPropType from "utils/onUserInteractionInitiatedPlaybackPropType";
import PropTypes from "prop-types";
import React from "react";
import Video from "containers/VideoComponent";
import { autobind } from "core-decorators";
import { noop } from "node-noop";
import Hammer from "components/HammerContainer";
import MediaContext from "context/MediaContext";
import { UseVideoDimensions } from "hooks/useVideoDimensions";
import { memoizeFocalPoint } from "utils/memoizeFocalPoint";
import VideoPoster from "./video/VideoPoster";

function focalPointPropType(props, propName, componentName) {
  if (props.useFocalPoint && !props.videoFocalPoint) {
    return new Error(
      `Missing prop \`videoFocalPoint\`. It is required in ${componentName}\` when useFocalPoint=true.`,
    );
  }
}

export default class VideoContainer extends React.Component {
  static displayName = "Container(Video)";

  static contextType = MediaContext;

  static propTypes = {
    active: PropTypes.bool,
    adPlaying: PropTypes.bool.isRequired,
    allowDurationChange: PropTypes.bool.isRequired,
    alwaysAutoplay: PropTypes.bool,
    autoplay: PropTypes.bool.isRequired,
    chapterId: PropTypes.number,
    containThreshold: PropTypes.number,
    dataAttributes: PropTypes.string,
    defaultDuration: PropTypes.number,
    destroyPlaybackStateOnUnmount: PropTypes.bool,
    disableAds: PropTypes.bool,
    disableContain: PropTypes.bool,
    fixedDuration: PropTypes.number,
    forceMute: PropTypes.bool,
    forceRestartBeforePlayback: PropTypes.bool,
    forcingChapterCover: PropTypes.bool,
    ignorePlayerOptionFitMode: PropTypes.bool,
    loop: PropTypes.bool,
    loopingVideosInactive: PropTypes.bool,
    manualContainerHeight: PropTypes.number,
    manualContainerWidth: PropTypes.number,
    measureVideoDimensions: PropTypes.bool.isRequired,
    onAdComplete: PropTypes.func,
    onComponentDidMount: PropTypes.func.isRequired,
    onEnded: PropTypes.func,
    onPlay: PropTypes.func,
    onReady: PropTypes.func,
    onSetAdTime: PropTypes.func.isRequired,
    onSetDuration: PropTypes.func.isRequired,
    onSetPlaying: PropTypes.func.isRequired,
    onSetSeeking: PropTypes.func.isRequired,
    onSetStalled: PropTypes.func.isRequired,
    onSetTime: PropTypes.func.isRequired,
    onSetUserInitiatedPause: PropTypes.func.isRequired,
    onTap: PropTypes.func.isRequired,
    onTimeUpdate: PropTypes.func,
    onTrackMediaProgress: PropTypes.func.isRequired,
    onUserInteractionInitiatedPlayback:
      onUserInteractionInitiatedPlaybackPropType,
    pixelRatio: PropTypes.number.isRequired,
    playAd: PropTypes.bool.isRequired,
    playAfterSeek: PropTypes.bool.isRequired,
    playing: PropTypes.bool.isRequired,
    playingSubchapter: PropTypes.bool,
    posterImageStored: PropTypes.bool.isRequired,
    renderChapterType: PropTypes.bool,
    renderVideoPosterComponent: PropTypes.func,
    scrubbing: PropTypes.bool.isRequired,
    seeking: PropTypes.bool.isRequired,
    showingChapterCover: PropTypes.bool,
    stateKey: PropTypes.string.isRequired,
    time: PropTypes.number.isRequired,
    timeOffset: PropTypes.number,
    useFocalPoint: PropTypes.bool,
    usePersistentVideo: PropTypes.bool.isRequired,
    usePlaybackResumeDelay: PropTypes.bool.isRequired,
    useStoryPreloadImage: PropTypes.bool,
    useTapToRestore: PropTypes.bool.isRequired,
    userInitiatedPause: PropTypes.bool.isRequired,
    videoFocalPoint: focalPointPropType,
    videoId: PropTypes.number.isRequired,
    waitingForInitialPlay: PropTypes.bool,
  };

  static defaultProps = {
    alwaysAutoplay: false,
    dataAttributes: null,
    disableAds: false,
    disableContain: false,
    forceMute: false,
    measureVideoDimensions: true,
    onReady: noop,
    renderChapterType: false,
    timeOffset: 0,
    useFocalPoint: false,
    waitingForInitialPlay: false,
  };

  playbackStartedTime = null;

  constructor(props) {
    super(props);

    this.state = {
      hasBeenInactive: !props.active && this.props.usePersistentVideo,
      hasBeenInactiveAndReceivedSeekedEvent: false,
      ready: false,
      showVideoPoster: true,
    };
  }

  componentDidMount() {
    window.addEventListener("keyup", this.handleKeyboardEvent);
    // reset some playback state on mount
    this.props.onComponentDidMount();
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    if (
      !newProps.active &&
      !this.state.hasBeenInactive &&
      this.props.usePersistentVideo
    ) {
      this.setState({
        hasBeenInactive: true,
        hasBeenInactiveAndReceivedSeekedEvent: false,
      });
    }

    if (
      this.props.active &&
      this.state.showVideoPoster &&
      this.playbackStartedTime != null &&
      Math.abs(newProps.time - this.playbackStartedTime) > 100 // milliseconds
    ) {
      this.setState({ showVideoPoster: false });
    }

    if (
      (newProps.active && !this.props.active) ||
      (this.playbackStartedTime == null && !this.state.hasBeenInactive)
    ) {
      this.playbackStartedTime = newProps.time;
    }

    // Set up to show the poster image if we're inactive and we've got a poster
    // image stored.
    if (
      this.props.posterImageStored &&
      !this.props.active &&
      !this.state.showVideoPoster
    ) {
      this.setState({ showVideoPoster: true });
    }
  }

  shouldComponentUpdate(newProps, newState) {
    let hasPropsChange = false;
    let hasStateChange = false;
    for (const key in omit(newProps, ["time"])) {
      if (this.props[key] !== newProps[key]) {
        hasPropsChange = true;
        break;
      }
    }

    for (const key in newState) {
      if (this.state[key] !== newState[key]) {
        hasStateChange = true;
        break;
      }
    }

    return hasPropsChange || hasStateChange;
  }

  componentDidUpdate(props) {
    const {
      loopingVideosInactive: wasLoopingVideosInactive,
      playingSubchapter: wasPlayingSubchapter,
    } = props;
    // Pause and resume looping videos automatically if we are not in a
    // user initiated pause.
    if (this.props.loop && !this.props.userInitiatedPause) {
      const isLoopingVideosInactive = this.props.loopingVideosInactive;

      if (wasLoopingVideosInactive && !isLoopingVideosInactive) {
        this.props.onSetPlaying(true);
      } else if (!wasLoopingVideosInactive && isLoopingVideosInactive) {
        this.props.onSetPlaying(false);
      }
    }

    if (!wasPlayingSubchapter && this.props.playingSubchapter) {
      this.props.onSetPlaying(false);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("keyup", this.handleKeyboardEvent);

    this.willUnmount = true;
  }

  @autobind
  handleKeyboardEvent(event) {
    if (this.props.active && keycode(event).toLowerCase() === "space") {
      // spacebar
      event.preventDefault();
      this.togglePlayState();
    }
  }

  @autobind
  handleDurationChange(value) {
    if (!this.props.adPlaying && this.props.allowDurationChange) {
      this.props.onSetDuration(value);
    }
  }

  @autobind
  togglePlayState() {
    const shouldPlay = !this.props.playing;
    this.props.onSetPlaying(shouldPlay);
    this.props.onSetUserInitiatedPause(!shouldPlay);
  }

  @autobind
  handleTimeUpdate(value, { isAdTime } = { isAdTime: false }) {
    // Reject timeupdate events while scrubbing.
    if (this.props.scrubbing || this.props.seeking) {
      return;
    }

    // If the player isn't ready and this is android, use this
    // timeupdate to signal that the player is now ready.
    if (!this.state.ready && this.props.usePlaybackResumeDelay && value > 0) {
      this.handleReady();

      return;
    }

    if (!this.state.ready) {
      return;
    }

    if (this.state.hasBeenInactiveAndReceivedSeekedEvent) {
      this.setState({
        hasBeenInactiveAndReceivedSeekedEvent: false,
      });

      delay(() => {
        this.setState({
          hasBeenInactive: false,
        });
      }, 250);
    }

    if (this.state.hasBeenInactive) {
      // Reject when we're not ready.
      return;
    }

    let newTime = value;
    if (
      isNumber(this.props.fixedDuration) &&
      newTime > this.props.fixedDuration
    ) {
      newTime = this.props.fixedDuration;
    }

    if ((this.props.playAd && this.props.adPlaying) || isAdTime) {
      this.props.onSetAdTime(newTime);
    } else {
      this.props.onSetTime(newTime + this.props.timeOffset);
    }

    if (isFunction(this.props.onTimeUpdate)) {
      this.props.onTimeUpdate(value);
    }

    this.props.onTrackMediaProgress();
  }

  @autobind
  handlePlay() {
    if (this.props.onPlay) {
      this.props.onPlay();
    }
  }

  @autobind
  handleReady() {
    if (this.state.ready) {
      return;
    }

    this.props.onReady();

    // Seek to wherever we want to be.
    const value = this.props.forceRestartBeforePlayback ? 0 : this.props.time;
    this.props.onSetTime(value);

    this.setState({
      ready: true,
    });
  }

  @autobind
  handleNotReady() {
    if (this.willUnmount) {
      return;
    }

    this.setState({
      ready: false,
    });
  }

  @autobind
  handleVideoPlaying() {
    this.props.onSetSeeking(false);
  }

  @autobind
  handleStalled() {
    this.props.onSetStalled(true);
  }

  @autobind
  handleNotStalled() {
    this.props.onSetStalled(false);
  }

  @autobind
  handleVideoSeeking() {
    this.props.onSetSeeking(true);
  }

  @autobind
  handleVideoSeeked() {
    this.props.onSetSeeking(false);

    if (this.state.hasBeenInactive) {
      const requiresWaitToSeek = this.props.time - this.props.timeOffset > 0;

      if (requiresWaitToSeek) {
        this.setState({
          hasBeenInactiveAndReceivedSeekedEvent: true,
        });
      } else {
        this.setState({
          hasBeenInactiveAndReceivedSeekedEvent: false,
          hasBeenInactive: false,
        });
      }
    }

    if (
      this.props.playAfterSeek &&
      !this.props.forcingChapterCover &&
      !this.props.scrubbing
    ) {
      this.props.onSetPlaying(true);
    }
  }

  render() {
    return (
      <UseVideoDimensions>
        {({ height: videoHeight, width: videoWidth }) => {
          const playerNotReady =
            this.props.usePlaybackResumeDelay && this.state.hasBeenInactive;
          const videoElement = (
            <Hammer
              onTap={this.props.useTapToRestore ? this.props.onTap : () => {}}
            >
              <span>
                <Video
                  active={this.props.active}
                  autoplay={this.props.autoplay}
                  canAutoplay={
                    !this.props.forcingChapterCover || this.props.alwaysAutoplay
                  }
                  chapterId={this.props.chapterId}
                  dataAttributes={this.props.dataAttributes}
                  defaultDuration={this.props.defaultDuration}
                  disableAds={this.props.disableAds}
                  forceMute={playerNotReady || this.props.forceMute}
                  handleNotReady={this.handleNotReady}
                  hide={playerNotReady}
                  loop={this.props.loop}
                  // onAdNotPlaying: this.props.onAdNotPlaying,
                  // onAdPlaying: this.props.onAdPlaying,
                  onAdComplete={this.props.onAdComplete}
                  onDurationChange={this.handleDurationChange}
                  onEnded={this.props.onEnded}
                  onNotStalled={this.handleNotStalled}
                  onPlay={this.handlePlay}
                  // Android needs to wait for a `timeupdate` event where the value of the
                  // time > 0 before it can safely be scrubbed. Don't send an `onReady` prop
                  // for Android; instead, call `this.handleReady` inside of
                  // `handleTimeUpdate`. See that function for the call to
                  // `this.handleReady()`.
                  onPlaying={this.handleVideoPlaying}
                  onReady={
                    this.props.usePlaybackResumeDelay ? noop : this.handleReady
                  }
                  onSeeked={this.handleVideoSeeked}
                  onSeeking={this.handleVideoSeeking}
                  onStalled={this.handleStalled}
                  onTimeUpdate={this.handleTimeUpdate}
                  onUserInteractionInitiatedPlayback={
                    this.props.onUserInteractionInitiatedPlayback
                  }
                  pixelRatio={this.props.pixelRatio}
                  showingChapterCover={this.props.showingChapterCover}
                  stateKey={this.props.stateKey}
                  timeOffset={this.props.timeOffset}
                  videoId={this.props.videoId}
                  waitingForInitialPlay={this.props.waitingForInitialPlay}
                />
              </span>
            </Hammer>
          );
          if (!this.props.useFocalPoint) {
            return videoElement;
          }

          const focalPoint = memoizeFocalPoint({
            ...this.props.videoFocalPoint,
            height: this.props.measureVideoDimensions
              ? videoHeight
              : this.props.videoFocalPoint.height,
            width: this.props.measureVideoDimensions
              ? videoWidth
              : this.props.videoFocalPoint.width,
          });

          return (
            <span
              {...(this.props.renderChapterType
                ? {
                    "aria-hidden": !this.props.active,
                    "data-chapter-type": "video",
                  }
                : null)}
            >
              <FocalPoint
                containThreshold={this.props.containThreshold}
                fitMode="cover"
                focalPoint={focalPoint}
                ignorePlayerOptionFitMode={this.props.ignorePlayerOptionFitMode}
                manualContainerHeight={this.props.manualContainerHeight}
                manualContainerWidth={this.props.manualContainerWidth}
                videoId={this.props.videoId}
              >
                {videoElement}
              </FocalPoint>
              {this.state.showVideoPoster &&
                (this.props.renderVideoPosterComponent ? (
                  this.props.renderVideoPosterComponent()
                ) : (
                  <VideoPoster
                    alwaysCover={!this.props.disableContain}
                    chapterId={this.props.chapterId}
                    disableAds={this.props.disableAds}
                    focalPoint={focalPoint}
                    manualContainerHeight={this.props.manualContainerHeight}
                    manualContainerWidth={this.props.manualContainerWidth}
                    stateKey={this.props.stateKey}
                    timeOffset={this.props.timeOffset}
                    useStoryPreloadImage={this.props.useStoryPreloadImage}
                    videoId={this.props.videoId}
                  />
                ))}
            </span>
          );
        }}
      </UseVideoDimensions>
    );
  }
}
